import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useId,
} from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItemText,
  IconButton,
  ListItemButton,
  ListItem,
} from '@mui/material';
import AurSearchBox from '../../../common/aur-search-box/AurSearchBox';

import {ReactComponent as ArrowRightOutlinedIcon} from '../../../../assests/svg/right-icon-large.svg';
import {ReactComponent as ArrowRightOutlinedIconActive} from '../../../../assests/svg/right-icon-large-active.svg';
import {ReactComponent as ArrowDoubbleRight} from '../../../../assests/svg/double-right-icon.svg';
import {ReactComponent as ArrowDoubbleRightInactive} from '../../../../assests/svg/double-icon-right-inactive.svg';
import {ReactComponent as ArrowLeftOutlinedIcon} from '../../../../assests/svg/left-icon-large.svg';
import {ReactComponent as ArrowLeftOutlinedIconActive} from '../../../../assests/svg/left-icon-large-active.svg';
import {ReactComponent as ArrowDoubbleLeft} from '../../../../assests/svg/double-left-icon.svg';
import {ReactComponent as ArrowDoubbleLeftActive} from '../../../../assests/svg/double-left-active.svg';
import {ReactComponent as ChackedIcon} from '../../../../assests/svg/checkmark-blue.svg';
import {ReactComponent as FilterList} from '../../../../assests/svg/filter-list.svg';
import {useDispatch, useSelector} from 'react-redux';
import {
  setAddDocsDrawer,
  setSelectedFieldsCount,
  setSelectedMetaField,
} from '../../../../redux/slices/indexingSlice';
import {t} from 'i18next';
import AurButton from '../../../common/aur-button/AurButton';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {useNavigate} from 'react-router-dom';
import {RootState} from '../../../../redux/store';
import {getDocListMetadata, getEcaFields} from '../../../../api/EcaServices';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {difference, intersection, union} from 'lodash';
import {isEmpty} from '../../../../utils/utils';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

type CustomIndexFormStep2Props = {
  setActiveStep?: any;
};

const CustomIndexFormStep2: FC<CustomIndexFormStep2Props> = (props) => {
  const {setActiveStep} = props;
  const {trackGAEvent} = useAnalytics();

  const [availableList, setAvailableList] = useState<any[]>([]);
  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );

  const fieldTemplateId = useSelector(
    (state: RootState) => state.indexes.createFieldTemplateId
  );

  const savedSelectedMetaField = useSelector(
    (state: RootState) => state.indexes.selectedMetaField
  );

  const [mappedFieldAnalyzerData, setMappedFieldAnalyzerData] = useState<any[]>(
    []
  );
  const [mappedFieldData, setMappedFieldData] = useState<any[]>([]);
  const [open, setOpen] = useState(false); // Dropdown open/close state
  const [selectedOption, setSelectedOption] = useState<any>(1);
  const [selectedFields, setSelectedFields] = useState<any[]>([]); // Store full field objects
  const [selectedAvailable, setSelectedAvailable] = useState<any[]>([]); // Store full field objects
  const [selectedSelected, setSelectedSelected] = useState<any[]>([]);
  const [searchTermAvailable, setSearchTermAvailable] = useState('');
  const [searchTermSelected, setSearchTermSelected] = useState('');
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null); // Reference to the dropdown menu
  const buttonRef = useRef<HTMLButtonElement | null>(null); // Reference to the button
  const [reqLoading, setReqLoading] = useState<boolean>(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  dispatch(setSelectedFieldsCount(selectedFields.length));

  const filterOptions = [
    {
      id: 1,
      label: t('filters.label.allFields'),
      fieldCategoryLabel: t('filters.category.all'),
    },
    {
      id: 2,
      label: t('filters.label.customMetadata'),
      fieldCategoryLabel: t('filters.category.customMetadata'),
    },
    {
      id: 3,
      label: t('filters.label.standardMetadata'),
      fieldCategoryLabel: t('filters.category.standardMetadata'),
    },
  ];

  const addFieldsHintID = useId();
  const addSelectedFieldsHintID = useId();
  const removeSelectedFieldsHintID = useId();
  const removeFieldsHintID = useId();
  const availableFields = useMemo(() => {
    const selectedKeys = new Set(selectedFields.map((s) => s.fieldId));
    return availableList.filter((field) => !selectedKeys.has(field.fieldId));
  }, [availableList, selectedFields]);

  useEffect(() => {
    if (savedSelectedMetaField?.length) {
      setSelectedFields(savedSelectedMetaField);
    }
  }, [savedSelectedMetaField]);

  const displayedAvailableFields = useMemo(() => {
    const fieldCategoryLabel = filterOptions.find(
      (option) => option.id === selectedOption
    )?.fieldCategoryLabel;

    const searchTerm = searchTermAvailable?.trim();
    return availableFields
      ?.filter((field) => {
        const matchesSearch = !isEmpty(searchTerm)
          ? field?.fieldLabel?.toLowerCase().includes(searchTerm.toLowerCase())
          : true;

        const matchesCategory =
          selectedOption === 1
            ? true
            : field.fieldCategoryLabel === fieldCategoryLabel;

        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => a?.fieldLabel?.localeCompare(b?.fieldLabel));
  }, [availableFields, searchTermAvailable, selectedOption]);

  const displayedSelectedFields = useMemo(() => {
    return selectedFields
      ?.filter((field) =>
        !isEmpty(searchTermSelected?.trim())
          ? field?.fieldLabel
              ?.toLowerCase()
              .includes(searchTermSelected.toLowerCase())
          : true
      )
      .sort((a, b) => a?.fieldLabel?.localeCompare(b?.fieldLabel));
  }, [selectedFields, searchTermSelected]);

  const commonAvailableFields = useMemo(() => {
    return intersection(availableFields, displayedAvailableFields);
  }, [availableFields, displayedAvailableFields]);

  const commonSelectedFields = useMemo(() => {
    return intersection(selectedFields, displayedSelectedFields);
  }, [displayedSelectedFields, selectedFields]);

  const commonSelectedAvailableFields = useMemo(() => {
    return intersection(selectedAvailable, displayedAvailableFields);
  }, [displayedAvailableFields, selectedAvailable]);

  const commonSelectedSelectedFields = useMemo(() => {
    return intersection(selectedSelected, displayedSelectedFields);
  }, [displayedSelectedFields, selectedSelected]);

  const trackAddRemoveSelectedFields = (action: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: action,
        type: GA_CLICK_TYPES.ICON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.indexManagement.newIndexFieldsSelected'),
      },
      subPage: {
        subpage_name: t('gaTracking.indexManagement.addMetadataFields'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };

  const moveToAllSelected = useCallback(() => {
    const updatedSelectedFields = union(selectedFields, commonAvailableFields);
    setSelectedAvailable([]);
    setSelectedSelected([]);
    trackAddRemoveSelectedFields(t('gaTracking.indexManagement.addFields'));
    setSelectedFields(updatedSelectedFields);
  }, [commonAvailableFields, selectedFields]);

  const moveToAllAvailable = useCallback(() => {
    const updatedSelectedFields = difference(
      selectedFields,
      commonSelectedFields
    );
    setSelectedAvailable([]);
    setSelectedSelected([]);
    trackAddRemoveSelectedFields(t('gaTracking.indexManagement.removeFields'));
    setSelectedFields(updatedSelectedFields);
  }, [selectedFields, commonSelectedFields]);

  const moveToSelected = useCallback(() => {
    const updated = selectedAvailable.filter(
      (f) => !selectedFields.some((s) => s.fieldId === f.fieldId)
    );
    const newSelectedFields = [...selectedFields, ...updated];
    setSelectedFields(newSelectedFields);
    trackAddRemoveSelectedFields(t('gaTracking.indexManagement.addFields'));
    setSelectedAvailable([]);
  }, [selectedAvailable, selectedFields]);

  const moveToAvailable = useCallback(() => {
    const newSelectedFields = selectedFields.filter(
      (f) => !selectedSelected.some((s) => s.fieldId === f.fieldId)
    );
    setSelectedFields(newSelectedFields);
    trackAddRemoveSelectedFields(t('gaTracking.indexManagement.removeFields'));
    setSelectedSelected([]);
  }, [selectedFields, selectedSelected]);

  const handleToggleSelected = (field: any) => {
    setSelectedSelected((prev) =>
      prev.includes(field)
        ? prev.filter((item) => item !== field)
        : [...prev, field]
    );
  };

  const handleToggleAvailable = (field: any) => {
    setSelectedAvailable((prev) =>
      prev.includes(field)
        ? prev.filter((item) => item !== field)
        : [...prev, field]
    );
  };

  const handleSearchChange = (
    search: string,
    type: 'available' | 'selected'
  ) => {
    if (type === 'available') {
      setSearchTermAvailable(search);
    } else {
      setSearchTermSelected(search);
    }
  };

  const isFieldSelected = (field: any, selectedList: any[]) =>
    selectedList.includes(field);

  const OpenPopupHandler = () => {
    setConfirmModal(true);
  };

  const handleContinueOptionSelected = () => {
    setConfirmModal(false);
    dispatch(setAddDocsDrawer(false));
    navigate('/indexmanagement');
  };

  const closeModalHandler = () => {
    setConfirmModal(false);
  };

  const nextbtnHandler = () => {
    dispatch(setSelectedMetaField(selectedFields));

    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.indexManagement.next'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.indexManagement.newIndexMetadataFieldsAdded'),
      },
      subPage: {
        subpage_name: t('gaTracking.indexManagement.addMetadataFields'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });

    setActiveStep((prev: any) => prev + 1);
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.indexManagement.addMetadataFields'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false); // Close the dropdown if clicking outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setOpen(!open);
  };

  const handleItemSelect = (item: any) => {
    setSearchTermAvailable(''); // Clear the search term when selecting an option
    setSelectedOption(item.id); // Update selected option
  };

  // step2
  const itemsPerPage = 3000;

  const fetchAvailableField = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}&top=${itemsPerPage}`;
    setReqLoading(true);
    try {
      const results = await getEcaFields(params);
      setReqLoading(true);
      const dataField = results?.data?.data;
      setMappedFieldData(dataField);
    } catch (e) {
      console.log(e);
      setReqLoading(false);
    } finally {
      setReqLoading(false);
    }
  };

  const fetchAvailableMetaData = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    try {
      const results = await getDocListMetadata(params, fieldTemplateId);

      setMappedFieldAnalyzerData(results.data);
    } catch (e) {
      console.log(e);
    }
  };
  useEffect(() => {
    fetchAvailableMetaData();
    fetchAvailableField();
  }, []);

  useEffect(() => {
    if (mappedFieldData.length > 0 && mappedFieldAnalyzerData.length > 0) {
      const newMappedData = mappedFieldAnalyzerData
        .map((item: any) => {
          const newFieldData = mappedFieldData.find(
            (field: any) =>
              field.fieldName == item.name && field.isSearchable === true
          );
          if (newFieldData !== undefined) {
            return {
              ...item,
              fieldId: newFieldData?.fieldId,
              fieldLabel: newFieldData?.fieldLabel,
              fieldCategoryLabel: newFieldData?.fieldCategoryLabel,
            };
          }
        })
        .filter((item) => item !== undefined);

      let sortedOrder = newMappedData;

      sortedOrder = [...sortedOrder]?.sort((a, b) => {
        if (a?.fieldLabel && b?.fieldLabel) {
          return a.fieldLabel.localeCompare(b.fieldLabel);
        } else {
          return a?.fieldLabel ? -1 : 1;
        }
      });
      setAvailableList(sortedOrder);
    }
  }, [mappedFieldAnalyzerData, mappedFieldData]);

  return (
    <div>
      <div className="columns-section">
        <Box
          className="availabe-fields"
          aria-label={t('accessibility.indexManagement.create.list')}
        >
          <div style={{height: '106px'}}>
            <Typography className="typo-text subhead4_size text mist-color-100 box-sub-heading margin-left-14">
              {t('eca.newExport.availableField')}
            </Typography>
            <div className="search-filter-container">
              <AurSearchBox
                dataTestId={'available-search-textfield'}
                classNameProps="searchbox-field-filter search-bar-center"
                onSearch={(e) => handleSearchChange(e, 'available')}
              />
              <IconButton
                className="shev-right"
                style={{marginRight: '12px'}}
                ref={buttonRef}
                onClick={toggleDropdown}
                aria-label={t(
                  'accessibility.indexManagement.create.filterHint'
                )}
              >
                <FilterList />
              </IconButton>
              {open && (
                <div
                  ref={menuRef}
                  role="listbox"
                  className="custom-index-field-filter-dropdown "
                  aria-label={t(
                    'accessibility.indexManagement.create.filterHint'
                  )}
                >
                  {filterOptions.map((item, index) => (
                    <div
                      key={item.id}
                      onClick={() => handleItemSelect(item)}
                      className={`dropdown-item ${
                        selectedOption === item.id ? 'selected' : ''
                      }`}
                      role="option"
                    >
                      <div
                        className="typo-text body2_size list-items"
                        style={{position: 'relative'}}
                      >
                        {item.label}
                        {selectedOption === item.id && (
                          <ChackedIcon
                            data-testid="check-icon"
                            aria-hidden="true"
                            style={{position: 'absolute', right: '0'}}
                          />
                        )}
                      </div>
                      {index == 0 && (
                        <Divider style={{marginTop: '15px'}}></Divider>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Divider></Divider>
          </div>
          <div className="names-list">
            <List>
              {displayedAvailableFields.map((field, index) => (
                <ListItem
                  key={`${field?.fieldLabel ?? ''}_${index}`}
                  disablePadding
                >
                  <ListItemButton
                    key={`${field?.fieldLabel ?? ''}_${index}`}
                    onClick={() => handleToggleAvailable(field)}
                    selected={selectedAvailable.includes(field)}
                    className="field-text"
                    data-testid={`available-${field?.fieldLabel}`}
                  >
                    <Typography className="body3_size mist-color-500">
                      <ListItemText
                        primary={
                          <Typography
                            component="span"
                            className="typo-text body3_size field-label-text"
                            noWrap
                          >
                            {field?.fieldLabel} &nbsp;
                          </Typography>
                        }
                        secondary={
                          <Typography
                            component="span"
                            className="typo-text body3_size field-type-text"
                            noWrap
                          >
                            ({field?.fieldCategoryLabel})
                          </Typography>
                        }
                      />
                    </Typography>
                    {isFieldSelected(field, selectedAvailable) && (
                      <ChackedIcon data-testid="check-icon" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </div>
        </Box>

        <div className="arrow-control">
          <div className="left-to-right">
            <span id={addFieldsHintID} className="sr-only">
              {t('ecaDocList.filterOptions.addAllFields')}
            </span>
            <IconButton
              onClick={moveToAllSelected}
              className="shev-right"
              aria-describedby={addFieldsHintID}
              disabled={isEmpty(displayedAvailableFields)}
              aria-label={t('accessibility.indexManagement.create.addAllArrow')}
              data-testid="move-all-available-button"
            >
              {isEmpty(displayedAvailableFields) ? (
                <ArrowDoubbleRightInactive />
              ) : (
                <ArrowDoubbleRight />
              )}
            </IconButton>
            <span id={addSelectedFieldsHintID} className="sr-only">
              {t('ecaDocList.filterOptions.addSelectedFields')}
            </span>
            <IconButton
              onClick={moveToSelected}
              data-testid="move-available-button"
              className="shev-right"
              aria-describedby={addSelectedFieldsHintID}
              disabled={isEmpty(commonSelectedAvailableFields)}
              aria-label={t(
                'accessibility.indexManagement.create.addAlllSingleArrow'
              )}
            >
              {!isEmpty(commonSelectedAvailableFields) ? (
                <ArrowRightOutlinedIconActive />
              ) : (
                <ArrowRightOutlinedIcon />
              )}
            </IconButton>
          </div>
          <Divider className="control-divider" />
          <div className="left-to-right">
            <span id={removeSelectedFieldsHintID} className="sr-only">
              {t('ecaDocList.filterOptions.removeSelectedFields')}
            </span>
            <IconButton
              onClick={moveToAvailable}
              data-testid="move-selected-button"
              className="shev-right"
              aria-describedby={removeSelectedFieldsHintID}
              disabled={isEmpty(commonSelectedSelectedFields)}
              aria-label={t(
                'accessibility.indexManagement.create.removeAllArrow'
              )}
            >
              {!isEmpty(commonSelectedSelectedFields) ? (
                <ArrowLeftOutlinedIconActive />
              ) : (
                <ArrowLeftOutlinedIcon />
              )}
            </IconButton>
            <span id={removeFieldsHintID} className="sr-only">
              {t('accessibility.indexManagement.create.removeAlllSingleArrow')}
            </span>
            <IconButton
              onClick={moveToAllAvailable}
              className="shev-right"
              aria-describedby={removeFieldsHintID}
              disabled={isEmpty(displayedSelectedFields)}
              aria-label={t(
                'accessibility.indexManagement.create.removeAlllSingleArrow'
              )}
              data-testid="move-all-selected-button"
            >
              {isEmpty(displayedSelectedFields) ? (
                <ArrowDoubbleLeft />
              ) : (
                <ArrowDoubbleLeftActive />
              )}
            </IconButton>
          </div>
        </div>

        <Box className="availabe-fields">
          <div style={{height: '106px'}}>
            <Typography className="typo-text subhead4_size text mist-color-100 box-sub-heading margin-left-14">
              {t('eca.newExport.selectedField')}
            </Typography>
            <AurSearchBox
              classNameProps="searchbox-field selected-search-bar"
              onSearch={(e) => handleSearchChange(e, 'selected')}
            />
            <Divider></Divider>
          </div>
          <div className="names-list">
            <List data-testid="selected-fields-list">
              {displayedSelectedFields.map((field, index) => (
                <ListItem key={`${field.fieldLabel}_${index}`} disablePadding>
                  <ListItemButton
                    key={`${field.fieldLabel}_${index}`}
                    onClick={() => handleToggleSelected(field)}
                    selected={selectedSelected.includes(field)}
                  >
                    <Typography className="body3_size mist-color-500">
                      <ListItemText primary={field.fieldLabel} />
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </div>
        </Box>
      </div>
      <Box className="index-action-container">
        <div className="custom-index-action-button">
          <AurButton
            type="exit"
            label={t('buttons.cancel')}
            title={t('buttons.cancel')}
            size="Medium"
            onClick={() => OpenPopupHandler()}
            ariaDescription={t(
              'accessibility.indexManagement.create.cancelHint'
            )}
          />

          <AurButton
            type="continue"
            label={t('buttons.next')}
            title={t('buttons.next')}
            size="Medium"
            onClick={nextbtnHandler}
            dataTestId={'closeBtn-test'}
            disabled={selectedFields.length === 0}
            ariaDescription={t(
              'accessibility.indexManagement.create.continueHint'
            )}
          />
        </div>
      </Box>

      {confirmModal && (
        <div>
          <AurPopup
            className="aur-unsaved-changes-popup add-doc-confirm-popup"
            open={confirmModal}
            onOk={handleContinueOptionSelected}
            onCancel={closeModalHandler}
            popupHeading={t('common.clearAllPopup.heading')}
            showPopupHeading={true}
            showPopupTitle={false}
            showCloseIcon={false}
            popupContent={t('indexes.customIndex.step1.confirmMessage')}
            labelActionCancel={t('common.clearAllPopup.buttonCancel')}
            labelActionOk={t('buttons.continue')}
            exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
            continueButtonHint={t('common.clearAllPopup.confirmToContinue')}
            cancelButtonTitle=""
          />
        </div>
      )}
      <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
    </div>
  );
};

export default CustomIndexFormStep2;
